// ===================================================================
// PA GRANTS — FEMA Forms
// Project Worksheet generator, RPA forms, Certifications
// ===================================================================

import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Paper, Text, Group, Stack, Badge, Title, Select, Button, Tabs, Table,
  Divider, Loader, Center, SimpleGrid,
} from '../../components/gab/GabComponents';
import { IconPrinter, IconFileDescription, IconCertificate, IconBuilding, IconPhoto, IconInfoCircle } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { PDFDocument, PDFTextField, PDFCheckBox } from 'pdf-lib';
import { PW_FIELDS, PHOTO_FIELDS, SCQ_FIELDS, RPA_FIELDS, CONTRACT_FIELDS } from './femaFieldMaps';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useMyPAOrgIds } from '../../hooks/useMyPAOrgData';
import { useRoleStore } from '../../store/roleStore';
import { useAuthStore } from '../../store/authStore';
import {
  DAMAGE_CATEGORY_LABELS, type DamageCategory,
} from '../../types/pagrants';
import { primary } from '../../lib/colors';

const APPLICANT_SLUGS = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'];

// V2 org mapper normalizes V2 field names to V1-style names used throughout this page
function normalizeOrg(org: Record<string, unknown> | null | undefined): Record<string, string> {
  if (!org) return {} as Record<string, string>;
  const o = org as Record<string, unknown>;
  return {
    ...(o as Record<string, string>),
    // V2 uses femaId; V1 used femaApplicantId
    femaApplicantId: String(o.femaId || o.femaApplicantId || ''),
    // V2 uses uei; V1 used ueiNumber
    ueiNumber: String(o.uei || o.ueiNumber || ''),
    // V2 uses address/city/state/zip for physical address
    physicalAddress: String(o.physicalAddress || o.address || ''),
    physicalCity: String(o.physicalCity || o.city || ''),
    physicalState: String(o.physicalState || o.state || ''),
    physicalZip: String(o.physicalZip || o.zip || ''),
    physicalCounty: String(o.physicalCounty || o.county || ''),
    // Contact fields — V2 may not have these; use notes as fallback
    primaryContact: String(o.primaryContact || ''),
    primaryTitle: String(o.primaryTitle || ''),
    primaryPhone: String(o.primaryPhone || o.phone || ''),
    altContact: String(o.altContact || ''),
    altTitle: String(o.altTitle || ''),
    altPhone: String(o.altPhone || ''),
    altEmail: String(o.altEmail || ''),
    einTaxId: String(o.einTaxId || ''),
    samRegistered: String(o.samRegistered || ''),
    samExpirationDate: String(o.samExpirationDate || ''),
    congressionalDistrict: String(o.congressionalDistrict || ''),
    pnpType: String(o.pnpType || ''),
    pnpTaxExemptVerified: String(o.pnpTaxExemptVerified || ''),
  } as Record<string, string>;
}

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const num = (s: string) => parseFloat(s) || 0;

function printDiv(el: HTMLElement | null) {
  if (!el) return;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>FEMA Form</title>
    <style>
      body { font-family: 'DM Sans', Arial, sans-serif; color: #333; max-width: 800px; margin: 0 auto; padding: 24px; }
      table { width: 100%; border-collapse: collapse; margin: 8px 0; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 12px; text-align: left; }
      th { background: #f0f0f0; font-weight: 600; }
      h1 { font-size: 18px; text-align: center; margin: 0; }
      h2 { font-size: 14px; background: var(--color-primary-800); color: #fff; padding: 4px 8px; margin: 16px 0 8px; }
      h3 { font-size: 13px; margin: 12px 0 4px; }
      .header { text-align: center; border-bottom: 3px solid var(--color-primary-800); padding-bottom: 8px; margin-bottom: 12px; }
      .field { display: flex; gap: 8px; margin: 2px 0; font-size: 12px; }
      .field-label { font-weight: 600; min-width: 180px; }
      .sig-line { border-bottom: 1px solid #333; min-width: 250px; display: inline-block; margin-top: 24px; }
      @media print { body { padding: 0; } }
    </style>
  </head><body>${el.innerHTML}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); w.close(); }, 250);
}

export default function FemaForms() {
  const {
    organizations, disasters, projects, projectCosts, compliance, insurance, documents,
    loadingProjects, fetchAll,
  } = usePAGrantsStore();
  const myOrgIds = useMyPAOrgIds();
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicantRole = APPLICANT_SLUGS.includes(effectiveRole?.roleSlug ?? '');

  // Scope data for applicant roles
  const scopedOrgs = useMemo(() =>
    isApplicantRole && myOrgIds.length > 0
      ? organizations.filter(o => myOrgIds.includes(String(o.Rid)))
      : organizations,
    [organizations, myOrgIds, isApplicantRole],
  );
  const scopedProjects = useMemo(() =>
    isApplicantRole && myOrgIds.length > 0
      ? projects.filter(p => myOrgIds.includes(String(p.organizationId)))
      : projects,
    [projects, myOrgIds, isApplicantRole],
  );

  const activeIncident = useAuthStore(s => s.activeIncident);

  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [selectedApplicant, setSelectedApplicant] = useState<string | null>(null);
  const [selectedDisaster, setSelectedDisaster] = useState<string | null>(null);

  const [packaging, setPackaging] = useState(false);
  const pwRef = useRef<HTMLDivElement>(null);
  const rpaRef = useRef<HTMLDivElement>(null);
  const certRef = useRef<HTMLDivElement>(null);
  const pnpRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLDivElement>(null);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-select disaster linked to global active incident
  useEffect(() => {
    if (activeIncident && disasters.length > 0) {
      const linked = disasters.find(d => String(d.incidentId) === String(activeIncident.id));
      if (linked) setSelectedDisaster(String(linked.Rid));
    }
  }, [activeIncident, disasters]);

  const orgMap = useMemo(() => {
    const m = new Map<number, typeof organizations[number]>();
    organizations.forEach(o => m.set(o.Rid, o));
    return m;
  }, [organizations]);

  const project = useMemo(() => projects.find(p => String(p.Rid) === selectedProject) ?? null, [projects, selectedProject]);
  const orgRaw = useMemo(() => project ? orgMap.get(Number(project.organizationId)) ?? null : null, [project, orgMap]);
  const org = useMemo(() => normalizeOrg(orgRaw as Record<string, unknown> | null), [orgRaw]);
  const disaster = useMemo(() => {
    if (selectedDisaster) return disasters.find(d => String(d.Rid) === selectedDisaster) ?? null;
    return disasters[0] ?? null;
  }, [disasters, selectedDisaster]);
  const costs = useMemo(() => project ? projectCosts.filter(c => c.projectId === String(project.Rid)) : [], [project, projectCosts]);
  const comp = useMemo(() => project ? compliance.find(c => c.projectId === String(project.Rid)) ?? null : null, [project, compliance]);
  const ins = useMemo(() => project ? insurance.filter(i => i.projectId === String(project.Rid)) : [], [project, insurance]);

  const selectedOrgObj = useMemo(() => {
    const raw = selectedOrg ? orgMap.get(Number(selectedOrg)) ?? null : null;
    return normalizeOrg(raw as Record<string, unknown> | null);
  }, [selectedOrg, orgMap]);

  const costTotal = costs.reduce((s, c) => s + num(c.totalCost), 0);

  const filteredProjectOptions = useMemo(() => {
    let list = scopedProjects;
    if (selectedDisaster) {
      list = list.filter(p => p.disasterId === selectedDisaster);
    }
    if (selectedApplicant) {
      list = list.filter(p => p.organizationId === selectedApplicant);
    }
    return list.map(p => {
      const pOrg = orgMap.get(Number(p.organizationId));
      return {
        value: String(p.Rid),
        label: `${p.projectNumber} — ${p.projectTitle}${pOrg ? ` (${pOrg.orgName})` : ''}`,
      };
    });
  }, [scopedProjects, selectedApplicant, selectedDisaster, orgMap]);

  if (loadingProjects && projects.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  const orgOptions = scopedOrgs.map(o => ({
    value: String(o.Rid),
    label: o.orgName,
  }));

  const disasterOptions = disasters.map(d => ({
    value: String(d.Rid),
    label: `DR-${d.disasterNumber} — ${d.disasterName || (d as any).declarationType || ''}`,
  }));

  function handleApplicantChange(val: string | null) {
    setSelectedApplicant(val);
    setSelectedProject(null);
  }

  function handleDisasterChange(val: string | null) {
    setSelectedDisaster(val);
    setSelectedProject(null);
  }

  // ── Shared field row component ─────────────────────────────
  const Field = ({ label, value }: { label: string; value: string }) => (
    <div className="field"><span className="field-label">{label}:</span><span>{value || '—'}</span></div>
  );

  if (isApplicantRole) {
    return (
      <Stack gap="lg">
        <Title order={3} c="black">FEMA Forms</Title>
        <Paper p="xl" withBorder ta="center">
          <Group justify="center" mb="sm">
            <IconInfoCircle size={24} color="var(--mantine-color-blue-6)" />
          </Group>
          <Text size="lg" fw={600} mb="xs">Form Generation Not Available</Text>
          <Text size="sm" c="dimmed">
            Contact your grantee to generate official FEMA forms. They will prepare and provide the completed
            forms for your projects.
          </Text>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Title order={3} c="black">FEMA Forms</Title>

      <Tabs defaultValue="pw">
        <Tabs.List>
          <Tabs.Tab value="pw" leftSection={<IconFileDescription size={14} />}>Project Worksheets</Tabs.Tab>
          <Tabs.Tab value="rpa">Request for PA</Tabs.Tab>
          <Tabs.Tab value="cert" leftSection={<IconCertificate size={14} />}>Certifications</Tabs.Tab>
          <Tabs.Tab value="pnp" leftSection={<IconBuilding size={14} />}>PNP Questionnaire</Tabs.Tab>
          <Tabs.Tab value="photos" leftSection={<IconPhoto size={14} />}>Photo Sheet</Tabs.Tab>
          <Tabs.Tab value="package">FEMA Package</Tabs.Tab>
        </Tabs.List>

        {/* ═══ PROJECT WORKSHEET ════════════════════════════ */}
        <Tabs.Panel value="pw" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable
              value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable
              value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable
              value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions}
              style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project}
              onClick={() => printDiv(pwRef.current)}>Print / PDF</Button>
          </Group>

          {!project ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select a project above to generate its Project Worksheet.</Text>
            </Paper>
          ) : (
            <Paper p="lg" radius="md" withBorder ref={pwRef}>
              {/* Header */}
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>PROJECT WORKSHEET</Text>
                <Group justify="center" gap="lg" mt={4}>
                  <Text size="xs">Disaster: DR-{disaster?.disasterNumber || '—'}</Text>
                  <Text size="xs">PA ID: {org?.femaApplicantId || '—'}</Text>
                  <Text size="xs">Project: {project.projectNumber}</Text>
                  <Text size="xs">Category: {project.category} — {DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}</Text>
                </Group>
              </div>

              {/* Section A: Applicant */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section A — Applicant Information
              </Text>
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Type" value={org?.orgType || '—'} />
                  <Field label="FIPS Code" value={org?.fipsCode || '—'} />
                  <Field label="FEMA Applicant ID" value={org?.femaApplicantId || '—'} />
                </Stack>
                <Stack gap={2}>
                  <Field label="UEI Number" value={org?.ueiNumber || '—'} />
                  <Field label="Congressional District" value={org?.congressionalDistrict || '—'} />
                  <Field label="Contact" value={org?.primaryContact || '—'} />
                  <Field label="Phone" value={org?.primaryPhone || '—'} />
                </Stack>
              </SimpleGrid>

              {/* Section B: Damage */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section B — Damage Description
              </Text>
              <Text size="xs" mb="xs"><strong>Location:</strong> {project.projectAddress}, {project.projectCity}, {project.projectState} {project.projectZip}</Text>
              <Text size="xs" mb="xs"><strong>Work Type:</strong> {project.workType} | <strong>Size:</strong> {project.projectSize?.toUpperCase()}</Text>
              <Paper p="sm" radius="sm" style={{ background: '#f8f9fa' }}>
                <Text size="xs">{project.damageDescription || 'No damage description provided.'}</Text>
              </Paper>

              {/* Section C: Scope of Work */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section C — Scope of Work
              </Text>
              <Paper p="sm" radius="sm" style={{ background: '#f8f9fa' }}>
                <Text size="xs">{project.scopeOfWork || 'No scope of work provided.'}</Text>
              </Paper>

              {/* Section D: Cost Estimate */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section D — Cost Estimate
              </Text>
              {costs.length > 0 ? (
                <Table striped withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Description</Table.Th>
                      <Table.Th ta="right">Qty</Table.Th>
                      <Table.Th>Unit</Table.Th>
                      <Table.Th ta="right">Unit Cost</Table.Th>
                      <Table.Th ta="right">Total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {costs.map(c => (
                      <Table.Tr key={c.Rid}>
                        <Table.Td><Text size="xs">{c.description}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{c.quantity}</Text></Table.Td>
                        <Table.Td><Text size="xs">{c.unit}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{fmt(num(c.unitCost))}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs" fw={600}>{fmt(num(c.totalCost))}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr style={{ background: '#e7f5ff' }}>
                      <Table.Td colSpan={4}><Text size="xs" fw={700}>TOTAL</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(costTotal)}</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="xs" c="dimmed">No cost line items recorded.</Text>
              )}

              {/* Section E: Cost Summary */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section E — Project Cost Summary
              </Text>
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="Estimated Cost" value={fmt(num(project.estimatedCost))} />
                  <Field label="Insurance Deduction" value={fmt(num(project.insuranceDeduction))} />
                  <Field label="Net Eligible Cost" value={fmt(num(project.netEligibleCost))} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Federal Share (75%)" value={fmt(num(project.federalShareAmount))} />
                  <Field label="Applicant Share (25%)" value={fmt(num(project.applicantShareAmount))} />
                  <Field label="Obligated Amount" value={fmt(num(project.obligatedAmount))} />
                </Stack>
              </SimpleGrid>

              {/* Section F: Hazard Mitigation (if applicable) */}
              {project.hazardMitigationIncluded === 'Yes' && (
                <>
                  <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                    Section F — Hazard Mitigation Proposal (Section 406)
                  </Text>
                  <Field label="Mitigation Cost" value={fmt(num(project.hazardMitigationCost))} />
                  <Paper p="sm" radius="sm" style={{ background: '#f8f9fa' }} mt={4}>
                    <Text size="xs">{project.hazardMitigationDescription || 'No description.'}</Text>
                  </Paper>
                </>
              )}

              {/* Compliance summary */}
              {comp && (
                <>
                  <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                    Environmental / Historic Compliance
                  </Text>
                  <SimpleGrid cols={2}>
                    <Stack gap={2}>
                      <Field label="NEPA Level" value={comp.nepaLevel} />
                      <Field label="NEPA Status" value={comp.nepaStatus} />
                      <Field label="Section 106 Required" value={comp.section106Required} />
                      <Field label="Section 106 Status" value={comp.section106Status} />
                    </Stack>
                    <Stack gap={2}>
                      <Field label="In Floodplain" value={comp.inFloodplain} />
                      <Field label="Flood Zone" value={comp.floodZone} />
                      <Field label="HAZMAT Present" value={comp.hazmatPresent} />
                      <Field label="Overall Status" value={comp.overallComplianceStatus} />
                    </Stack>
                  </SimpleGrid>
                </>
              )}

              {/* Insurance summary */}
              {ins.length > 0 && (
                <>
                  <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                    Insurance Information
                  </Text>
                  {ins.map(i => (
                    <SimpleGrid cols={2} key={i.Rid} mb="xs">
                      <Stack gap={2}>
                        <Field label="Carrier" value={i.carrier} />
                        <Field label="Policy #" value={i.policyNumber} />
                        <Field label="Coverage" value={fmt(num(i.coverageAmount))} />
                      </Stack>
                      <Stack gap={2}>
                        <Field label="Claim Filed" value={i.claimFiled} />
                        <Field label="FEMA Deduction" value={fmt(num(i.femaDeductionAmount))} />
                        <Field label="O&M Required" value={i.obtainMaintainRequired} />
                      </Stack>
                    </SimpleGrid>
                  ))}
                </>
              )}
            </Paper>
          )}
        </Tabs.Panel>

        {/* ═══ REQUEST FOR PUBLIC ASSISTANCE ════════════════ */}
        <Tabs.Panel value="rpa" pt="md">
          <Group mb="md">
            <Select size="sm" placeholder="Select an applicant..." searchable clearable
              value={selectedOrg} onChange={setSelectedOrg} data={orgOptions}
              style={{ flex: 1, maxWidth: 400 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!selectedOrg}
              onClick={() => printDiv(rpaRef.current)}>Print / PDF</Button>
          </Group>

          {!selectedOrg ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select an applicant to generate the Request for Public Assistance form.</Text>
            </Paper>
          ) : (
            <Paper p="lg" radius="md" withBorder ref={rpaRef}>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>REQUEST FOR PUBLIC ASSISTANCE</Text>
                <Text size="xs" c="dimmed">FEMA Form 90-49</Text>
              </div>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Disaster Information
              </Text>
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="Disaster Number" value={`DR-${disaster?.disasterNumber || '—'}`} />
                  <Field label="Disaster Name" value={disaster?.disasterName || '—'} />
                  <Field label="Declaration Date" value={disaster?.declarationDate || '—'} />
                </Stack>
                <Stack gap={2}>
                  <Field label="State" value={disaster?.state || '—'} />
                  <Field label="Designated Counties" value={disaster?.designatedCounties || '—'} />
                  <Field label="Incident Period" value={`${disaster?.incidentPeriodStart || '—'} to ${disaster?.incidentPeriodEnd || '—'}`} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Applicant Information
              </Text>
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="Applicant Name" value={selectedOrgObj.orgName} />
                  <Field label="Type" value={selectedOrgObj.orgType} />
                  <Field label="Role" value={selectedOrgObj.orgRole} />
                  <Field label="FIPS Code" value={selectedOrgObj.fipsCode} />
                  <Field label="FEMA Applicant ID" value={selectedOrgObj.femaApplicantId} />
                </Stack>
                <Stack gap={2}>
                  <Field label="UEI Number" value={selectedOrgObj.ueiNumber} />
                  <Field label="EIN/Tax ID" value={selectedOrgObj.einTaxId} />
                  <Field label="SAM Registered" value={selectedOrgObj.samRegistered} />
                  <Field label="SAM Expiration" value={selectedOrgObj.samExpirationDate} />
                  <Field label="Congressional District" value={selectedOrgObj.congressionalDistrict} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Address
              </Text>
              <Text size="xs">
                {selectedOrgObj.physicalAddress}, {selectedOrgObj.physicalCity}, {selectedOrgObj.physicalState} {selectedOrgObj.physicalZip}
              </Text>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Authorized Representative
              </Text>
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="Name" value={selectedOrgObj.primaryContact} />
                  <Field label="Title" value={selectedOrgObj.primaryTitle} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Email" value={selectedOrgObj.primaryEmail} />
                  <Field label="Phone" value={selectedOrgObj.primaryPhone} />
                </Stack>
              </SimpleGrid>

              {selectedOrgObj.orgType === 'nonprofit' && (
                <>
                  <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                    Private Nonprofit Information
                  </Text>
                  <SimpleGrid cols={2}>
                    <Stack gap={2}>
                      <Field label="PNP Type" value={selectedOrgObj.pnpType} />
                      <Field label="Tax Exempt Verified" value={selectedOrgObj.pnpTaxExemptVerified} />
                    </Stack>
                    <Stack gap={2}>
                      <Field label="Open to Public" value={selectedOrgObj.pnpOpenToPublic} />
                      <Field label="Critical Facility" value={selectedOrgObj.pnpCriticalFacility} />
                    </Stack>
                  </SimpleGrid>
                  <Field label="Services" value={selectedOrgObj.pnpServicesDescription} />
                </>
              )}

              <Divider my="lg" />
              <Text size="xs" fw={600}>Signature of Authorized Representative:</Text>
              <Group mt="xl" gap="xl">
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30 }} />
                  <Text size="xs" c="dimmed">Signature</Text>
                </Stack>
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 150, height: 30 }} />
                  <Text size="xs" c="dimmed">Date</Text>
                </Stack>
              </Group>
            </Paper>
          )}
        </Tabs.Panel>

        {/* ═══ CERTIFICATIONS ══════════════════════════════ */}
        <Tabs.Panel value="cert" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable
              value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable
              value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable
              value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions}
              style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project}
              onClick={() => printDiv(certRef.current)}>Print / PDF</Button>
          </Group>

          {!project ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select a project to generate its Applicant Certification.</Text>
            </Paper>
          ) : (
            <Paper p="lg" radius="md" withBorder ref={certRef}>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>APPLICANT&apos;S CERTIFICATION</Text>
                <Text size="xs">Project {project.projectNumber} — {project.projectTitle}</Text>
              </div>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Project Identification
              </Text>
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="Disaster" value={`DR-${disaster?.disasterNumber || '—'}`} />
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Project Number" value={project.projectNumber} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Category" value={`${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`} />
                  <Field label="Estimated Cost" value={fmt(num(project.estimatedCost))} />
                  <Field label="FEMA Project ID" value={project.femaProjectId} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="lg" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Certifications
              </Text>
              <Stack gap="md" mt="sm">
                {[
                  { label: '1. Legal Responsibility', desc: 'I certify that the applicant has the legal authority to apply for federal assistance and the institutional, managerial, and financial capability to ensure proper planning, management, and completion of the project.', field: project.certLegalResponsibility },
                  { label: '2. Insurance Compliance', desc: 'I certify that the applicant will comply with all applicable insurance requirements for federally assisted facilities, including the requirement to obtain and maintain insurance coverage as a condition of receiving federal disaster assistance.', field: project.certInsuranceCompliance },
                  { label: '3. Procurement Compliance', desc: 'I certify that the applicant will comply with applicable federal procurement standards under 2 CFR Part 200, including requirements for competition, cost/price analysis, and documentation of procurement activities.', field: project.certProcurementCompliance },
                  { label: '4. No Duplication of Work', desc: 'I certify that the work described in this project does not duplicate any other federal, state, or local assistance, and that the applicant will not seek or receive duplicate assistance for the same work.', field: project.certNoWorkDuplication },
                ].map(cert => (
                  <Paper key={cert.label} p="sm" radius="sm" style={{ background: '#f8f9fa' }}>
                    <Group gap="xs" mb={4}>
                      <Badge size="sm" color={cert.field ? 'green' : 'gray'} variant="light">
                        {cert.field || 'Pending'}
                      </Badge>
                      <Text size="xs" fw={700}>{cert.label}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">{cert.desc}</Text>
                  </Paper>
                ))}
              </Stack>

              <Divider my="lg" />
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="Certified By" value={project.certSignedBy || '(not yet signed)'} />
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30, marginTop: 16 }} />
                  <Text size="xs" c="dimmed">Signature of Authorized Representative</Text>
                </Stack>
                <Stack gap={2}>
                  <Field label="Date" value={project.certSignedDate || '(not yet signed)'} />
                  <div style={{ borderBottom: '1px solid #333', width: 200, height: 30, marginTop: 16 }} />
                  <Text size="xs" c="dimmed">Title</Text>
                </Stack>
              </SimpleGrid>
            </Paper>
          )}
        </Tabs.Panel>
        {/* ═══ PNP FACILITY QUESTIONNAIRE (009-0-121) ════════ */}
        <Tabs.Panel value="pnp" pt="md">
          <Group mb="md">
            <Select size="sm" placeholder="Select an applicant..." searchable clearable
              value={selectedOrg} onChange={setSelectedOrg} data={orgOptions}
              style={{ flex: 1, maxWidth: 400 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!selectedOrg}
              onClick={() => printDiv(pnpRef.current)}>Print / PDF</Button>
          </Group>

          {!selectedOrg ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select an applicant to generate the PNP Facility Questionnaire.</Text>
            </Paper>
          ) : selectedOrgObj.orgType !== 'pnp' && selectedOrgObj.orgType !== 'nonprofit' ? (
            <Paper p="xl" withBorder ta="center">
              <Badge color="yellow" mb="sm">Not a PNP</Badge>
              <Text c="dimmed">This form is only applicable to Private Non-Profit (PNP) organizations.</Text>
              <Text size="xs" c="dimmed" mt="xs">{selectedOrgObj.orgName} is classified as &quot;{selectedOrgObj.orgType}&quot;.</Text>
            </Paper>
          ) : (
            <Paper p="lg" radius="md" withBorder ref={pnpRef}>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>PRIVATE NON-PROFIT (PNP) FACILITY QUESTIONNAIRE</Text>
                <Text size="xs" c="dimmed">FEMA Form 009-0-121</Text>
              </div>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section A — Applicant Information
              </Text>
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="Applicant Name" value={selectedOrgObj.orgName} />
                  <Field label="FEMA Applicant ID" value={selectedOrgObj.femaApplicantId} />
                  <Field label="UEI Number" value={selectedOrgObj.ueiNumber} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Disaster Number" value={`DR-${disaster?.disasterNumber || '—'}`} />
                  <Field label="Contact" value={selectedOrgObj.primaryContact} />
                  <Field label="Phone" value={selectedOrgObj.primaryPhone} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section B — Organization Classification
              </Text>
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="PNP Type / Category" value={selectedOrgObj.pnpType} />
                  <Field label="Tax Exempt Status Verified" value={selectedOrgObj.pnpTaxExemptVerified} />
                  <Field label="Tax Exempt Letter on File" value={selectedOrgObj.pnpTaxExemptLetter} />
                </Stack>
                <Stack gap={2}>
                  <Field label="EIN / Tax ID" value={selectedOrgObj.einTaxId} />
                  <Field label="SAM Registered" value={selectedOrgObj.samRegistered} />
                  <Field label="SAM Expiration" value={selectedOrgObj.samExpirationDate} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section C — Facility Information
              </Text>
              <Stack gap={4}>
                <Field label="Open to General Public" value={selectedOrgObj.pnpOpenToPublic} />
                <Field label="Critical Facility" value={selectedOrgObj.pnpCriticalFacility} />
                <Field label="Services Provided" value={selectedOrgObj.pnpServicesDescription} />
              </Stack>
              <Paper p="sm" radius="sm" mt="xs" style={{ background: '#f8f9fa' }}>
                <Text size="xs" c="dimmed" fs="italic">
                  A PNP facility must provide an essential service of a governmental nature to the general public
                  or be a facility that is open to the general public and provides a critical service (education, emergency,
                  medical, custodial care, utility, or other essential government-type service).
                </Text>
              </Paper>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section D — Eligibility Determination
              </Text>
              <Stack gap={4}>
                <Text size="xs">
                  <strong>1.</strong> Is the organization a PNP as defined by 44 CFR 206.221(e)?{' '}
                  <Badge size="xs" color={selectedOrgObj.pnpTaxExemptVerified === 'Yes' ? 'green' : 'gray'} variant="light">
                    {selectedOrgObj.pnpTaxExemptVerified === 'Yes' ? 'YES' : 'PENDING'}
                  </Badge>
                </Text>
                <Text size="xs">
                  <strong>2.</strong> Does the facility provide services that are otherwise a responsibility of government?{' '}
                  <Badge size="xs" color={selectedOrgObj.pnpOpenToPublic === 'Yes' ? 'green' : 'gray'} variant="light">
                    {selectedOrgObj.pnpOpenToPublic === 'Yes' ? 'YES' : 'PENDING'}
                  </Badge>
                </Text>
                <Text size="xs">
                  <strong>3.</strong> Is the facility a critical service facility (education, emergency, medical, custodial care, utility)?{' '}
                  <Badge size="xs" color={selectedOrgObj.pnpCriticalFacility === 'Yes' ? 'green' : 'gray'} variant="light">
                    {selectedOrgObj.pnpCriticalFacility === 'Yes' ? 'YES' : 'PENDING'}
                  </Badge>
                </Text>
              </Stack>

              {/* Projects for this PNP */}
              {(() => {
                const pnpProjects = projects.filter(p => String(p.organizationId) === selectedOrg);
                return pnpProjects.length > 0 ? (
                  <>
                    <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                      Section E — Associated Projects
                    </Text>
                    <Table striped withTableBorder withColumnBorders>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Project #</Table.Th>
                          <Table.Th>Title</Table.Th>
                          <Table.Th>Category</Table.Th>
                          <Table.Th ta="right">Est. Cost</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {pnpProjects.map(p => (
                          <Table.Tr key={p.Rid}>
                            <Table.Td><Text size="xs">{p.projectNumber}</Text></Table.Td>
                            <Table.Td><Text size="xs">{p.projectTitle}</Text></Table.Td>
                            <Table.Td><Text size="xs">{p.category} — {DAMAGE_CATEGORY_LABELS[p.category as DamageCategory] || ''}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="xs">{fmt(num(p.estimatedCost))}</Text></Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </>
                ) : null;
              })()}

              <Divider my="lg" />
              <Text size="xs" fw={600}>Certification:</Text>
              <Text size="xs" c="dimmed" mt="xs">
                I certify that the information provided above is true and correct. The facility described qualifies
                as a Private Non-Profit facility and provides services or functions of a governmental nature.
              </Text>
              <Group mt="xl" gap="xl">
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30 }} />
                  <Text size="xs" c="dimmed">Signature of Authorized Representative</Text>
                </Stack>
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 150, height: 30 }} />
                  <Text size="xs" c="dimmed">Date</Text>
                </Stack>
              </Group>
            </Paper>
          )}
        </Tabs.Panel>
        {/* ═══ PHOTO DOCUMENTATION SHEET (009-0-91D) ════════════ */}
        <Tabs.Panel value="photos" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable
              value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable
              value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable
              value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions}
              style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project}
              onClick={() => printDiv(photoRef.current)}>Print / PDF</Button>
          </Group>

          {!project ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select a project to generate its Photo Documentation Sheet.</Text>
            </Paper>
          ) : (
            <Paper p="lg" radius="md" withBorder ref={photoRef}>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>PHOTO DOCUMENTATION SHEET</Text>
                <Text size="xs" c="dimmed">FEMA Form 009-0-91D</Text>
                <Group justify="center" gap="lg" mt={4}>
                  <Text size="xs">Disaster: DR-{disaster?.disasterNumber || '—'}</Text>
                  <Text size="xs">PA ID: {org?.femaApplicantId || '—'}</Text>
                  <Text size="xs">Project: {project.projectNumber}</Text>
                </Group>
              </div>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Project Information
              </Text>
              <SimpleGrid cols={2}>
                <Stack gap={2}>
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Project Title" value={project.projectTitle} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Location" value={`${project.projectAddress}, ${project.projectCity}, ${project.projectState} ${project.projectZip}`} />
                  <Field label="Category" value={`${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Photographs
              </Text>
              {(() => {
                const projDocs = documents.filter(d =>
                  d.projectId === String(project.Rid) &&
                  (d.mimeType?.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp)$/i.test(d.documentName || ''))
                );
                if (projDocs.length === 0) {
                  return <Text size="xs" c="dimmed">No photos uploaded for this project.</Text>;
                }
                return (
                  <SimpleGrid cols={2} spacing="md">
                    {projDocs.map((doc, idx) => {
                      const thumbSrc = doc.notes?.startsWith('data:') ? doc.notes : '';
                      return (
                        <Paper key={doc.Rid} p="sm" withBorder>
                          <Text size="xs" fw={600} mb={4}>Photo {idx + 1}</Text>
                          <div style={{ background: '#f0f0f0', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, marginBottom: 8, overflow: 'hidden' }}>
                            {thumbSrc ? (
                              <img
                                src={thumbSrc}
                                alt={doc.documentName}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <Text size="xs" c="dimmed">No preview available</Text>
                            )}
                          </div>
                          <Text size="xs"><strong>File:</strong> {doc.documentName}</Text>
                          <Text size="xs"><strong>Date:</strong> {doc.uploadDate || '—'}</Text>
                          <Text size="xs"><strong>Description:</strong> {doc.description || '—'}</Text>
                          {doc.geotagLat && doc.geotagLng && (
                            <Text size="xs"><strong>GPS:</strong> {doc.geotagLat}, {doc.geotagLng}</Text>
                          )}
                        </Paper>
                      );
                    })}
                  </SimpleGrid>
                );
              })()}

              <Divider my="lg" />
              <Text size="xs" c="dimmed">
                Photos document the location, nature, and extent of damage to the facility described in the Project Worksheet.
                Pre-disaster photos should be included when available.
              </Text>
            </Paper>
          )}
        </Tabs.Panel>

        {/* ═══ FEMA PACKAGE EXPORT ══════════════════════════════ */}
        <Tabs.Panel value="package" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable
              value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable
              value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable
              value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions}
              style={{ flex: 1, maxWidth: 500 }} />
          </Group>

          {!project ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select a project to generate the FEMA submission package.</Text>
            </Paper>
          ) : (
            <Stack gap="md">
              <Paper p="lg" withBorder>
                <Text fw={600} mb="sm">FEMA Submission Package — {project.projectNumber}</Text>
                <Text size="sm" c="dimmed" mb="md">
                  Generate a complete set of official FEMA forms filled with project data.
                  Uses the actual government PDF templates with form fields populated from your data.
                </Text>

                <Stack gap="xs" mb="md">
                  <Text size="sm" fw={600}>Package Contents:</Text>
                  {([
                    ['009-0-91', 'Project Worksheet', true],
                    ['009-0-91D', 'Photo Documentation Sheet', true],
                    ['009-0-120', 'Special Consideration Questions', !!comp],
                    ['009-0-49', 'Request for Public Assistance', !!org],
                    ['009-0-126', 'Contract Work Summary', true],
                  ] as [string, string, boolean][]).map(([form, label, ready]) => (
                    <Group key={form} gap="xs">
                      <Badge size="xs" color={ready ? 'green' : 'gray'} variant="filled" circle>
                        {ready ? '✓' : '—'}
                      </Badge>
                      <Text size="sm">{form} — {label}</Text>
                    </Group>
                  ))}
                </Stack>

                <Button
                  size="sm"
                  loading={packaging}
                  leftSection={<IconPrinter size={14} />}
                  onClick={async () => {
                    setPackaging(true);
                    try {
                      const { default: JSZip } = await import('jszip');
                      const zip = new JSZip();

                      // Helper: load a FEMA PDF template and return the doc + form
                      async function loadTemplate(formId: string) {
                        const resp = await fetch(`/fema-forms/${formId}.pdf`);
                        if (!resp.ok) throw new Error(`Template ${formId} not found`);
                        const bytes = await resp.arrayBuffer();
                        const doc = await PDFDocument.load(bytes);
                        const form = doc.getForm();
                        return { doc, form };
                      }

                      // Helper: safely set a text field value (truncate to prevent crash on huge strings)
                      function setText(form: ReturnType<PDFDocument['getForm']>, fieldName: string, value: string) {
                        try {
                          const field = form.getField(fieldName);
                          if (field instanceof PDFTextField) {
                            // Truncate to 2000 chars max — large strings can crash pdf-lib
                            const safe = (value || '').substring(0, 2000);
                            field.setText(safe);
                          }
                        } catch { /* field not found — skip */ }
                      }

                      // Helper: safely check a checkbox
                      function setCheck(form: ReturnType<PDFDocument['getForm']>, fieldName: string, checked: boolean) {
                        try {
                          const field = form.getField(fieldName);
                          if (field instanceof PDFCheckBox) {
                            if (checked) field.check(); else field.uncheck();
                          }
                        } catch { /* field not found — skip */ }
                      }

                      const fmtC = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

                      // ── 1. Project Worksheet (009-0-91) ──────────────
                      const { doc: pwDoc, form: pwForm } = await loadTemplate('009-0-91');
                      setText(pwForm, PW_FIELDS.disasterNo, disaster?.disasterNumber || '');
                      setText(pwForm, PW_FIELDS.drNumber, disaster?.disasterNumber || '');
                      setText(pwForm, PW_FIELDS.paId, org?.femaApplicantId || '');
                      setText(pwForm, PW_FIELDS.category, `${project.category} - ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`);
                      setText(pwForm, PW_FIELDS.date, new Date().toLocaleDateString());
                      setText(pwForm, PW_FIELDS.projectNo, project.projectNumber);
                      setText(pwForm, PW_FIELDS.applicant, org?.orgName || '');
                      setText(pwForm, PW_FIELDS.county, project.projectCounty || project.projectCity || '');
                      setText(pwForm, PW_FIELDS.location, `${project.projectAddress || ''}, ${project.projectCity || ''}, ${project.projectState || ''} ${project.projectZip || ''}`);
                      setText(pwForm, PW_FIELDS.latitude, project.projectLat || '');
                      setText(pwForm, PW_FIELDS.longitude, project.projectLng || '');
                      setText(pwForm, PW_FIELDS.damagedFacility, project.projectTitle);
                      setText(pwForm, PW_FIELDS.workCompletePct, project.completionPctEstimate || '');
                      setText(pwForm, PW_FIELDS.damageDesc, project.damageDescription || '');
                      setText(pwForm, PW_FIELDS.scopeOfWork, project.scopeOfWork || '');
                      // Checkboxes
                      if (project.hazardMitigationIncluded === 'Yes') setCheck(pwForm, PW_FIELDS.hazMitYes, true);
                      else setCheck(pwForm, PW_FIELDS.hazMitNo, true);
                      if (ins.length > 0) setCheck(pwForm, PW_FIELDS.insuranceYes, true);
                      else setCheck(pwForm, PW_FIELDS.insuranceNo, true);
                      if (comp) setCheck(pwForm, PW_FIELDS.specialConsYes, true);
                      else setCheck(pwForm, PW_FIELDS.specialConsNo, true);
                      // Cost estimate rows (9 data rows available)
                      costs.slice(0, 9).forEach((c, i) => {
                        const row = PW_FIELDS.costRows[i];
                        if (!row) return;
                        setText(pwForm, row.item, String(i + 1));
                        setText(pwForm, row.narrative, c.description);
                        setText(pwForm, row.qty, `${c.quantity} ${c.unit || ''}`);
                        setText(pwForm, row.unitPrice, fmtC(num(c.unitCost)));
                        setText(pwForm, row.cost, fmtC(num(c.totalCost)));
                      });
                      setText(pwForm, PW_FIELDS.totalCost, fmtC(costTotal));
                      setText(pwForm, PW_FIELDS.preparedBy, project.assignedSpecialist || '');
                      pwForm.flatten();
                      zip.file('01-Project-Worksheet-009-0-91.pdf', await pwDoc.save());

                      // ── 2. Photo Documentation Sheet (009-0-91D) ─────
                      try {
                      const { doc: phDoc, form: phForm } = await loadTemplate('009-0-91D');
                      setText(phForm, PHOTO_FIELDS.disasterNo, disaster?.disasterNumber || '');
                      setText(phForm, PHOTO_FIELDS.drNumber, disaster?.disasterNumber || '');
                      setText(phForm, PHOTO_FIELDS.paId, org?.femaApplicantId || '');
                      setText(phForm, PHOTO_FIELDS.category, project.category);
                      setText(phForm, PHOTO_FIELDS.date, new Date().toLocaleDateString());
                      setText(phForm, PHOTO_FIELDS.projectNo, project.projectNumber);
                      setText(phForm, PHOTO_FIELDS.applicant, org?.orgName || '');
                      setText(phForm, PHOTO_FIELDS.county, project.projectCounty || project.projectCity || '');
                      const projPhotos = documents.filter(d => d.projectId === String(project.Rid) && (d.mimeType || '').startsWith('image'));
                      const photoFields = [PHOTO_FIELDS.photo1, PHOTO_FIELDS.photo2, PHOTO_FIELDS.photo3, PHOTO_FIELDS.photo4];
                      projPhotos.slice(0, 4).forEach((d, i) => {
                        setText(phForm, photoFields[i], [
                          `Photo ${i + 1}: ${d.documentName}`,
                          `Date: ${d.uploadDate || 'N/A'}`,
                          `By: ${d.uploadedBy || 'N/A'}`,
                          d.description || '',
                        ].filter(Boolean).join('\n'));
                      });
                      phForm.flatten();
                      zip.file('02-Photo-Sheet-009-0-91D.pdf', await phDoc.save());
                      } catch (e) { console.warn('91D failed:', e); }

                      // ── 3. Special Consideration Questions (009-0-120) ─
                      if (comp) {
                        const { doc: scqDoc, form: scqForm } = await loadTemplate('009-0-120');
                        setText(scqForm, SCQ_FIELDS.applicant, org?.orgName || '');
                        setText(scqForm, SCQ_FIELDS.paId, org?.femaApplicantId || '');
                        setText(scqForm, SCQ_FIELDS.date, new Date().toLocaleDateString());
                        setText(scqForm, SCQ_FIELDS.projectName, project.projectTitle);
                        setText(scqForm, SCQ_FIELDS.projectNo, project.projectNumber);
                        setText(scqForm, SCQ_FIELDS.location, project.projectAddress || '');
                        // Parse compliance notes for answers
                        let scqNotes: Record<string, { answer?: string; comment?: string }> = {};
                        try {
                          const parsed = JSON.parse(comp.complianceNotes || '{}');
                          Object.entries(parsed).forEach(([k, v]) => {
                            if (typeof v === 'string') scqNotes[k] = { comment: v };
                            else if (typeof v === 'object' && v !== null) scqNotes[k] = v as any;
                          });
                        } catch { /* ignore */ }
                        // Map compliance data to SCQ questions (matching form order)
                        // Q1: Insurance, Q2: Floodplain, Q3: Coastal Barrier, Q4: Change pre-disaster,
                        // Q5: Hazard mitigation, Q6: Historic, Q7: Pristine, Q8: HAZMAT, Q9: Other environmental
                        const scqData = [
                          { answer: comp.floodInsuranceRequired, comment: scqNotes.scq1?.comment },
                          { answer: comp.inFloodplain, comment: scqNotes.scq2?.comment },
                          { answer: comp.coastalBarrierArea, comment: scqNotes.scq3?.comment },
                          { answer: comp.nepaRequired, comment: scqNotes.scq4?.comment },
                          { answer: comp.esaConsultationRequired, comment: scqNotes.scq5?.comment },
                          { answer: comp.historicProperty, comment: scqNotes.scq6?.comment },
                          { answer: comp.wetlandsImpacted, comment: scqNotes.scq7?.comment },
                          { answer: comp.hazmatPresent, comment: scqNotes.scq8?.comment },
                          { answer: comp.coastalZone, comment: scqNotes.scq9?.comment },
                        ];
                        scqData.forEach((q, i) => {
                          const qf = SCQ_FIELDS.questions[i];
                          if (!qf) return;
                          const ans = (q.answer || '').toLowerCase();
                          if (ans === 'yes') setCheck(scqForm, qf.yes, true);
                          else if (ans === 'no') setCheck(scqForm, qf.no, true);
                          else if (ans === 'n/a' || ans === 'na' || ans === 'not_required' || ans === 'unsure') setCheck(scqForm, qf.unsure, true);
                          if (q.comment) setText(scqForm, qf.comment, q.comment);
                        });
                        scqForm.flatten();
                        zip.file('03-Special-Considerations-009-0-120.pdf', await scqDoc.save());
                      }

                      // ── 4. Request for Public Assistance (009-0-49) ──
                      if (org) {
                        const { doc: rpaDoc, form: rpaForm } = await loadTemplate('009-0-49');
                        // Top section
                        setText(rpaForm, RPA_FIELDS.applicantName, org.orgName);
                        setText(rpaForm, RPA_FIELDS.dateSubmitted, disaster?.declarationDate || new Date().toLocaleDateString());
                        setText(rpaForm, RPA_FIELDS.county, org.physicalCounty || org.physicalCity || '');
                        // Physical location
                        setText(rpaForm, RPA_FIELDS.streetAddress, org.physicalAddress || '');
                        setText(rpaForm, RPA_FIELDS.city, org.physicalCity || '');
                        setText(rpaForm, RPA_FIELDS.physCounty, org.physicalCounty || '');
                        setText(rpaForm, RPA_FIELDS.state, org.physicalState || '');
                        setText(rpaForm, RPA_FIELDS.zip, org.physicalZip || '');
                        // Mailing address
                        setText(rpaForm, RPA_FIELDS.mailStreet, org.mailingAddress || '');
                        setText(rpaForm, RPA_FIELDS.mailCity, org.mailingCity || '');
                        setText(rpaForm, RPA_FIELDS.mailState, org.mailingState || '');
                        setText(rpaForm, RPA_FIELDS.mailZip, org.mailingZip || '');
                        // Primary contact
                        setText(rpaForm, RPA_FIELDS.primaryName, org.primaryContact || '');
                        setText(rpaForm, RPA_FIELDS.primaryTitle, org.primaryTitle || '');
                        setText(rpaForm, RPA_FIELDS.primaryPhone, org.primaryPhone || '');
                        setText(rpaForm, RPA_FIELDS.primaryEmail, org.primaryEmail || '');
                        // Alternate contact
                        setText(rpaForm, RPA_FIELDS.altName, org.altContact || '');
                        setText(rpaForm, RPA_FIELDS.altTitle, org.altTitle || '');
                        setText(rpaForm, RPA_FIELDS.altPhone, org.altPhone || '');
                        setText(rpaForm, RPA_FIELDS.altEmail, org.altEmail || '');
                        // PNP checkbox
                        const ot = (org.orgType || '').toLowerCase();
                        if (ot === 'pnp' || ot === 'nonprofit') {
                          setCheck(rpaForm, RPA_FIELDS.pnpYes, true);
                          setText(rpaForm, RPA_FIELDS.pnpFacilityType, org.pnpType || '');
                        } else {
                          setCheck(rpaForm, RPA_FIELDS.pnpNo, true);
                        }
                        // Official use footer
                        setText(rpaForm, RPA_FIELDS.officialFema, disaster?.disasterNumber || '');
                        setText(rpaForm, RPA_FIELDS.officialDr, disaster?.disasterNumber || '');
                        rpaForm.flatten();
                        zip.file('04-Request-for-PA-009-0-49.pdf', await rpaDoc.save());
                      }

                      // ── 5. Contract Work Summary (009-0-126) ─────────
                      try {
                      const { doc: cwDoc, form: cwForm } = await loadTemplate('009-0-126');
                      setText(cwForm, CONTRACT_FIELDS.date, new Date().toLocaleDateString());
                      setText(cwForm, CONTRACT_FIELDS.paId, org?.femaApplicantId || '');
                      setText(cwForm, CONTRACT_FIELDS.projectNo, project.projectNumber);
                      setText(cwForm, CONTRACT_FIELDS.disasterNo, disaster?.disasterNumber || '');
                      setText(cwForm, CONTRACT_FIELDS.location, `${project.projectAddress || ''}, ${project.projectCity || ''}`);
                      setText(cwForm, CONTRACT_FIELDS.category, project.category);
                      setText(cwForm, CONTRACT_FIELDS.description, project.scopeOfWork || project.projectTitle);
                      setText(cwForm, CONTRACT_FIELDS.pageNo, '1');
                      setText(cwForm, CONTRACT_FIELDS.ofPages, '1');
                      const contractCosts = costs.filter(c => c.costType === 'contracts');
                      let contractTotal = 0;
                      contractCosts.slice(0, 8).forEach((c, i) => {
                        const row = CONTRACT_FIELDS.rows[i];
                        if (!row) return;
                        setText(cwForm, row.contractor, c.sourceReference || c.description);
                        setText(cwForm, row.invoice, c.description);
                        setText(cwForm, row.amount, fmtC(num(c.totalCost)));
                        setText(cwForm, row.comments, c.notes || '');
                        contractTotal += num(c.totalCost);
                      });
                      setText(cwForm, CONTRACT_FIELDS.grandTotal, fmtC(contractTotal));
                      setText(cwForm, CONTRACT_FIELDS.certifiedBy, project.assignedSpecialist || '');
                      cwForm.flatten();
                      zip.file('05-Contract-Work-Summary-009-0-126.pdf', await cwDoc.save());
                      } catch (e) { console.warn('126 failed:', e); }

                      // Generate ZIP and download
                      const blob = await zip.generateAsync({ type: 'blob' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `FEMA-Package-${project.projectNumber}-${new Date().toISOString().split('T')[0]}.zip`;
                      a.click();
                      URL.revokeObjectURL(url);
                      notifications.show({ message: `FEMA package downloaded — ${Object.keys(zip.files).length} filled PDFs`, color: 'green' });
                    } catch (err) {
                      notifications.show({ message: `Package failed: ${err instanceof Error ? err.message : 'Unknown'}`, color: 'red' });
                    } finally { setPackaging(false); }
                  }}
                >
                  Download FEMA Package (ZIP)
                </Button>
              </Paper>
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
